import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterableDataTable, DrawerPanel, ConfirmDialog } from '../../components/shared';
import type { RowAction, DrawerAction } from '../../components/shared';
import { SafetyWarningControllerApi } from '../../api-generated/apis';
import { apiConfig } from '../../api-generated/apiConfig';

interface SafetyWarning {
  id: string;
  title: string;
  description: string;
  severity: string;
}

const warningApi = new SafetyWarningControllerApi(apiConfig);

const columns: GridColDef[] = [
  { field: 'title', headerName: 'Title', flex: 1, minWidth: 200 },
  { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 250 },
  { field: 'severity', headerName: 'Severity', flex: 0.5, minWidth: 100 },
];

async function fetchWarnings(): Promise<SafetyWarning[]> {
  return warningApi.getAll1() as any;
}

async function saveWarning(warning: Partial<SafetyWarning>, isEdit: boolean): Promise<SafetyWarning> {
  if (isEdit) {
    return warningApi.update1({ id: warning.id!, safetyWarning: warning as any }) as any;
  }
  return warningApi.create1({ safetyWarning: warning as any }) as any;
}

async function deleteWarning(id: string): Promise<void> {
  return warningApi.delete1({ id });
}

const SafetyWarningsPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingWarning, setEditingWarning] = useState<SafetyWarning | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSeverity, setFormSeverity] = useState('WARNING');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['safety-warnings'], queryFn: fetchWarnings });

  const saveMutation = useMutation({
    mutationFn: () => saveWarning({ id: editingWarning?.id, title: formTitle, description: formDescription, severity: formSeverity }, !!editingWarning),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['safety-warnings'] }); setDrawerOpen(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWarning(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['safety-warnings'] }); setConfirmOpen(false); },
  });

  const handleAdd = useCallback(() => {
    setEditingWarning(null); setFormTitle(''); setFormDescription(''); setFormSeverity('WARNING'); setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row: SafetyWarning) => {
    setEditingWarning(row); setFormTitle(row.title || ''); setFormDescription(row.description || ''); setFormSeverity(row.severity || 'WARNING'); setDrawerOpen(true);
  }, []);

  const rowActions: RowAction<SafetyWarning>[] = [
    { label: 'Edit', icon: EditIcon, onClick: handleEdit },
    { label: 'Delete', icon: DeleteIcon, onClick: (row) => { setDeleteId(row.id); setConfirmOpen(true); } },
  ];

  const drawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setDrawerOpen(false) },
    { label: editingWarning ? 'Update' : 'Create', variant: 'contained', onClick: () => saveMutation.mutate(), disabled: !formTitle },
  ];

  return (
    <>
      <FilterableDataTable<SafetyWarning>
        rows={data ?? []}
        columns={columns}
        loading={isLoading}
        totalCount={data?.length ?? 0}
        page={0}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={rowActions}
        primaryAction={{ label: 'Add Warning', onClick: handleAdd }}
        searchPlaceholder="Search warnings..."
      />
      <DrawerPanel open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editingWarning ? 'Edit Warning' : 'New Safety Warning'} width="narrow" actions={drawerActions}>
        <TextField label="Title" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} fullWidth size="small" sx={{ mb: 2 }} />
        <TextField label="Description" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} fullWidth size="small" multiline rows={3} sx={{ mb: 2 }} />
        <TextField label="Severity" value={formSeverity} onChange={(e) => setFormSeverity(e.target.value)} fullWidth size="small" select>
          <MenuItem value="CRITICAL">Critical</MenuItem>
          <MenuItem value="WARNING">Warning</MenuItem>
          <MenuItem value="INFO">Info</MenuItem>
        </TextField>
      </DrawerPanel>
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={() => deleteId && deleteMutation.mutate(deleteId)} title="Delete Warning" message="Are you sure?" confirmLabel="Delete" confirmColor="error" />
    </>
  );
};

export default SafetyWarningsPanel;
